/**
 * React hook for real-time Firebase listener on current action
 * Feature: 003-c-program-files - Send Action to Device
 */

import { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase';
import { CurrentAction } from '../types/currentAction';

/**
 * Hook to subscribe to current action changes in Firebase
 *
 * @param deviceId - The device ID to monitor
 * @returns Object with currentAction data, loading state, and error state
 */
export function useCurrentAction(deviceId: string) {
  const [currentAction, setCurrentAction] = useState<CurrentAction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Another device: never show the previous device's action while this one loads
    setCurrentAction(null);
    setLoading(true);
    if (!deviceId) {
      setLoading(false);
      return;
    }

    const actionRef = ref(database, `/devices/${deviceId}/status/currentAction`);

    // Subscribe to real-time updates
    const unsubscribe = onValue(
      actionRef,
      (snapshot) => {
        setLoading(false);
        setError(null);

        if (snapshot.exists()) {
          setCurrentAction(snapshot.val() as CurrentAction);
        } else {
          setCurrentAction(null);
        }
      },
      (err) => {
        setLoading(false);
        setError(err as Error);
        console.error('Error listening to current action:', err);
      }
    );

    // Cleanup on unmount - CRITICAL for preventing memory leaks
    return () => unsubscribe();
  }, [deviceId]);

  return { currentAction, loading, error };
}
